import {
  DOCUMENT,
  inject,
  Injectable,
  Injector,
  SecurityContext,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { select } from '@ngxs/store';
import { distinctUntilChanged } from 'rxjs';

import { getContrastRatio, normalizeHex } from '@cosmos/util-color';
import { injectDestroyRef } from '@cosmos/util-common/di';
import { CustomerPortalPresentationQueries } from '@customer-portal/presentations/data-access-presentations';
import { DEFAULT_PRESENTATION_FONT } from '@esp/presentations/page-modules/types-common';
import {
  TEMPLATE_VARIABLE_CSS_MAP,
  type TemplateVariables,
} from '@website-modules/types-common';

import { injectTemplateFontHelper } from '../utils/presentation.util';

const DOCUMENT_WITH_TEMPLATE_VARIABLES_CLASS_NAME =
  'esp-presentation-with-template-variables';

const PRIMARY_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW =
  'esp-presentation-primary-color-on-white-bg-is-low-contrast';
const SECONDARY_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW =
  'esp-presentation-secondary-color-on-white-bg-is-low-contrast';
const TERTIARY_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW =
  'esp-presentation-tertiary-color-on-white-bg-is-low-contrast';
const HIGHLIGHT_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW =
  'esp-presentation-highlight-color-on-white-bg-is-low-contrast';
const ALL_COLORS_ON_WHITE_BG_CONTRAST_IS_LOW = [
  PRIMARY_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW,
  SECONDARY_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW,
  TERTIARY_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW,
  HIGHLIGHT_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW,
];

const DEFAULT_PRIMARY_COLOR = '#6A7281';

/**
 * The template font overrides all three theme font variables, so the whole
 * presentation (headings and body) renders in the selected family.
 */
const FONT_FAMILY_CSS_VARS = [
  '--font-family-primary',
  '--font-family-secondary',
  '--font-family-tertiary',
];

const MINIMAL_TEXT_CONTRAST_RATIO = 3;

@Injectable({
  providedIn: 'root',
})
export class PresentationTemplateStylesService {
  private readonly _templateVars = select(
    CustomerPortalPresentationQueries.getTemplateVariables
  );

  private readonly _templateCustomStyle = select(
    CustomerPortalPresentationQueries.getCustomStyles
  );
  private _styleElement: HTMLStyleElement | null = null;
  private readonly _document = inject(DOCUMENT);
  private readonly _injector = inject(Injector);
  private readonly _domSanitizer = inject(DomSanitizer);
  private readonly _destroyRef = injectDestroyRef();
  private readonly _fontHelper = injectTemplateFontHelper();

  init(): void {
    this._listenForTemplateChangesAndUpdateCssVars();
    this._destroyRef.onDestroy(() => {
      this._removeCssVarsFromDocument();
    });
  }

  private _listenForTemplateChangesAndUpdateCssVars(): void {
    toObservable(this._templateVars, { injector: this._injector })
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
      .subscribe((templateVariables) => {
        if (!templateVariables) {
          this._removeCssVarsFromDocument();
          return;
        }
        this._setCssVarsInDocument();
      });

    toObservable(this._templateCustomStyle, { injector: this._injector })
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
      .subscribe((templateCustomStyle) => {
        if (!templateCustomStyle) {
          this._removeCustomStyleFromDocument();
          return;
        }
        this._setCustomStyleInDocument(templateCustomStyle);
      });
  }

  private _getTemplateVariables(): TemplateVariables {
    const templateVariables = this._templateVars();
    return {
      ...templateVariables,
      // download the default primary color if it's not provided
      PrimaryColor: templateVariables?.PrimaryColor ?? DEFAULT_PRIMARY_COLOR,
    } as TemplateVariables;
  }

  private _setCssVarsInDocument(): void {
    const document = this._document;
    const templateVariables = this._getTemplateVariables();

    Object.entries(templateVariables).forEach(
      ([k, colorValue]: [string, string]) => {
        // the font is not a color and has its own set of css variables
        if (k === 'PrimaryFont') {
          return;
        }
        const variableName =
          TEMPLATE_VARIABLE_CSS_MAP[k as keyof TemplateVariables] ?? k;
        document.documentElement.style.setProperty(variableName, colorValue);
      }
    );

    this._setFontFamilyInDocument();

    if (this._templateVars()?.PrimaryColor) {
      document.documentElement.style.setProperty(
        '--provided-template-primary-color',
        `var(${TEMPLATE_VARIABLE_CSS_MAP.PrimaryColor})`
      );
    }

    document.documentElement.classList.add(
      DOCUMENT_WITH_TEMPLATE_VARIABLES_CLASS_NAME
    );

    this._setContrastRatioClasses(templateVariables);
  }

  private _setFontFamilyInDocument(): void {
    const family =
      this._templateVars()?.PrimaryFont || DEFAULT_PRESENTATION_FONT;

    // fallback family keeps the presentation readable if the font can't be fetched
    const fontFamilyValue = `${family}, sans-serif`;
    FONT_FAMILY_CSS_VARS.forEach((variableName) => {
      this._document.documentElement.style.setProperty(
        variableName,
        fontFamilyValue
      );
    });

    this._fontHelper.preloadTemplateFont();
  }

  private _removeCssVarsFromDocument(): void {
    const document = this._document;

    [
      ...Object.values(TEMPLATE_VARIABLE_CSS_MAP),
      ...FONT_FAMILY_CSS_VARS,
    ].forEach((variableName) => {
      document.documentElement.style.removeProperty(variableName);
    });
    document.documentElement.style.removeProperty(
      '--provided-template-primary-color'
    );

    document.documentElement.classList.remove(
      DOCUMENT_WITH_TEMPLATE_VARIABLES_CLASS_NAME
    );
    this._removeContrastRatioClasses();
  }

  private _setCustomStyleInDocument(customStyle: string): void {
    const sanitizedCss = this._domSanitizer.sanitize(
      SecurityContext.STYLE,
      customStyle
    );

    if (!sanitizedCss) return; // the content is considered unsafe
    const document = this._document;
    if (!this._styleElement) {
      this._styleElement = document.createElement('style');
    }
    this._styleElement.textContent = sanitizedCss;
    document.head.appendChild(this._styleElement);
  }

  private _removeCustomStyleFromDocument(): void {
    if (this._styleElement) {
      this._document.head.removeChild(this._styleElement);
      this._styleElement = null;
    }
  }

  private _setContrastRatioClasses(templateVariables: TemplateVariables): void {
    const document = this._document;

    // first make sure we remove all contrast ratio classes added previously
    this._removeContrastRatioClasses();

    const options = [
      {
        lowContrastClassName: PRIMARY_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW,
        color: templateVariables.PrimaryColor,
      },
      {
        lowContrastClassName: SECONDARY_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW,
        color: templateVariables.SecondaryColor,
      },
      {
        lowContrastClassName: TERTIARY_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW,
        color: templateVariables.TertiaryColor,
      },
      {
        lowContrastClassName: HIGHLIGHT_COLOR_ON_WHITE_BG_CONTRAST_IS_LOW,
        color: templateVariables.HighlightColor,
      },
    ];

    for (const option of options) {
      if (!option.color) continue;
      const [colorAsHex] = normalizeHex(option.color, this._document);
      const contrastRatioForColorOnWhiteBg = getContrastRatio(
        '#ffffff',
        colorAsHex
      );
      if (contrastRatioForColorOnWhiteBg < MINIMAL_TEXT_CONTRAST_RATIO) {
        document.documentElement.classList.add(option.lowContrastClassName);
      }
    }
  }

  private _removeContrastRatioClasses(): void {
    const document = this._document;
    ALL_COLORS_ON_WHITE_BG_CONTRAST_IS_LOW.forEach((className) =>
      document.documentElement.classList.remove(className)
    );
  }
}
